// ---------------------------------------------------------------------------
// Template preview panel — react-pdf with debounced width for no flicker
// ---------------------------------------------------------------------------
import { useDebounce, useMeasure } from '@uidotdev/usehooks';
import { AlertCircle, Code, FileText, Loader2, MessageSquare } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { PdfRenderer } from '@/components/templates-v2/document-generation/pdf/renderer/PdfFormRenderer';
import type { ReadTemplateDetail } from '@/services/api/generated/templatesV2Service';

interface TemplatePreviewPanelProps {
  template: ReadTemplateDetail;
}

export const TemplatePreviewPanel = ({
  template,
}: TemplatePreviewPanelProps) => {
  const { format } = template;

  // HTML/SMS — the editor replaces the preview panel on the detail page.
  // This component is only reached in the drawer context, where we show a simple message.
  if (format === 'HTML') {
    return (
      <EmptyState
        icon={<Code className="h-8 w-8 text-zinc-500" />}
        title="HTML template"
        description="Open the full editor to view and edit this template."
      />
    );
  }

  if (format === 'SMS') {
    return (
      <EmptyState
        icon={<MessageSquare className="h-8 w-8 text-zinc-500" />}
        title="SMS template"
        description="Open the full editor to view and edit this template."
      />
    );
  }

  // EXTERNAL — link only
  if (format === 'EXTERNAL') {
    return (
      <EmptyState
        icon={<FileText className="h-8 w-8 text-zinc-500" />}
        title="External template"
        description={
          template.preview_render_url
            ? 'Use the preview link to view this template.'
            : 'Preview is not available for external templates.'
        }
      />
    );
  }

  const isRendering =
    template.sample_render_status === 'QUEUED' ||
    template.sample_render_status === 'RENDERING';

  // PDF / DOCX — stored preview (prefer rendered PDF, fall back to image)
  // Rendering overlay wraps existing preview content
  const renderingOverlay = isRendering ? (
    <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 dark:bg-zinc-900/60">
      <div className="flex flex-col items-center gap-2">
        <Loader2 className="h-6 w-6 animate-spin text-zinc-500" />
        <span className="text-xs text-zinc-500">Generating preview...</span>
      </div>
    </div>
  ) : null;

  if (template.preview_render_url) {
    if (template.preview_render_url.endsWith('.pdf') || format === 'PDF' || format === 'DOCX') {
      return (
        <div className="relative h-full">
          {renderingOverlay}
          <StoredPdfPreview url={template.preview_render_url} />
        </div>
      );
    }

    // Non-PDF render (e.g. HTML preview) — embed inline
    return (
      <div className="relative h-full">
        {renderingOverlay}
        <iframe
          src={template.preview_render_url}
          title="Template preview"
          className="h-full w-full border-0"
          sandbox="allow-same-origin"
        />
      </div>
    );
  }

  if (template.preview_image_url) {
    return (
      <div className="relative p-3">
        {renderingOverlay}
        <img
          src={template.preview_image_url}
          alt="Template preview"
          className="w-full rounded"
        />
      </div>
    );
  }

  // No preview available — show spinner if rendering, otherwise empty state
  if (isRendering) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <Loader2 className="mb-3 h-8 w-8 animate-spin text-zinc-500" />
        <p className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
          Generating preview...
        </p>
      </div>
    );
  }

  return (
    <EmptyState
      icon={<AlertCircle className="h-8 w-8 text-zinc-500" />}
      title="No preview available"
      description="Click Re-analyze to generate a preview."
    />
  );
};

/**
 * Fetches PDF blob and renders via react-pdf with annotation support.
 * Width is debounced (300ms) so dragging the resize handle doesn't cause
 * react-pdf to destroy/recreate every page canvas on each pixel of movement.
 * Blob is cached by URL so polling refetches don't trigger re-renders.
 */
const StoredPdfPreview = ({ url }: { url: string }) => {
  const [containerRef, { width }] = useMeasure();
  const debouncedWidth = useDebounce(width, 300);
  const renderWidth = debouncedWidth || 600;

  const [blob, setBlob] = useState<Blob | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Cache blob by URL — skip re-fetch if URL hasn't changed
  const fetchedUrlRef = useRef<string | null>(null);

  useEffect(() => {
    if (url === fetchedUrlRef.current && blob) return undefined;

    let cancelled = false;
    setLoading(true);
    setError(null);

    fetch(url)
      .then((r) => {
        if (!r.ok) throw new Error('Failed to fetch preview');
        return r.blob();
      })
      .then((b) => {
        if (!cancelled) {
          fetchedUrlRef.current = url;
          setBlob(b);
        }
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [url, blob]);

  if (loading && !blob) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-zinc-500" />
      </div>
    );
  }

  if (error && !blob) {
    return (
      <EmptyState
        icon={<AlertCircle className="h-8 w-8 text-red-500" />}
        title="Preview failed"
        description={error ?? 'Unable to load preview'}
      />
    );
  }

  if (!blob) return null;

  return (
    <div ref={containerRef} className="h-full overflow-y-auto">
      <PdfRenderer file={blob} renderWidth={renderWidth} />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Shared empty state
// ---------------------------------------------------------------------------

const EmptyState = ({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) => (
  <div className="flex flex-col items-center justify-center py-16 text-center">
    <div className="mb-3">{icon}</div>
    <p className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
      {title}
    </p>
    <p className="mt-1 text-xs font-medium text-zinc-500">{description}</p>
  </div>
);
